import React, { useEffect, useState } from "react"
import {
  Card,
  Col,
  Container,
  Row,
  CardBody,
  CardTitle,
  Button,
  Form,
  Label,
  FormFeedback,
  Input,
  FormText,
} from "reactstrap"
import { Link, useNavigate, useParams } from "react-router-dom"
import leftArrow from "../../assets/images/icons/left-arrow.svg"
import LoaderComponent from "components/loader"
import Spinners from "components/Common/Spinner"
import * as Yup from "yup"
import { useFormik } from "formik"
import toast from "react-hot-toast"
import KycDocumentUpload from "pages/KYC/kycDocumentUpload"
import {
  useEditDocumentMutation,
  useLazyGetDocumentQuery,
} from "store/api/documents/documents"
import Breadcrumb from "components/Common/Breadcrumb"
import UploadedFileBox from "pages/KYC/uploadedFileBox"
import { documentTypes } from "helpers/utils"

const DocumentEdit = ({ user_type }) => {
  const { id } = useParams()
  const [getResult, { isFetching }] = useLazyGetDocumentQuery()
  const [update, { isFetching: isUpdating }] = useEditDocumentMutation()
  const [details, setDetails] = useState(null)
  const navigate = useNavigate()

  document.title = "Document Details | Prachay"

  const getData = async () => {
    try {
      const { success, data } = await getResult({ id }).unwrap()
      if (success) {
        setDetails(data)
        validation.setFieldValue("title", data?.title)
        validation.setFieldValue("status", data?.status)
        validation.setFieldValue("docType", data?.doc_type)
      }
    } catch (error) {}
  }

  const editFaq = async (values) => {
    try {
      const { success, message, data } = await update({
        id,
        ...values,
        user_type,
      }).unwrap()
      if (success) {
        navigate(
          user_type === "INVESTOR"
            ? "/resources/sample-documents"
            : "/wp-documents"
        )
        toast.success(message, { id: "FaqSuccess" })
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        validation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  useEffect(() => {
    if (id) {
      getData()
    }
  }, [id])

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { title: "", status: "", document: "", docType: "" },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      await editFaq(values)
    },
  })

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Update Document" />
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  {isFetching ? (
                    <LoaderComponent />
                  ) : (
                    <Form
                      className="form-horizontal"
                      onSubmit={(e) => {
                        e.preventDefault()
                        validation.handleSubmit()
                        return false
                      }}
                    >
                      <Row className="mb-2">
                        <Col sm={6} className="mb-2">
                          <Label className="form-label">
                            Document Title<span className="error">*</span>
                          </Label>
                          <Input
                            name="title"
                            className="form-control"
                            placeholder={"Enter Document Title"}
                            type="text"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.title}
                            invalid={
                              validation.touched.title &&
                              validation.errors.title
                            }
                          />
                          {validation.touched.title &&
                          validation.errors.title ? (
                            <FormFeedback type="invalid">
                              {validation.errors.title}
                            </FormFeedback>
                          ) : null}
                        </Col>

                        <Col xl={6} className="mb-2">
                          <Label className="form-label">
                            Status<span className="error">*</span>
                          </Label>
                          <select
                            name="status"
                            className="form-select pageSize mb-2"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.status}
                          >
                            <option value={""}>Select Status</option>
                            {[
                              { label: "ACTIVE", value: "ACTIVE" },
                              { label: "DISABLE", value: "DISABLE" },
                            ].map((item, index) => (
                              <option key={index} value={item.value}>
                                {item.label}
                              </option>
                            ))}
                          </select>
                          {validation.touched.status &&
                          validation.errors.status ? (
                            <FormFeedback type="invalid">
                              {validation.errors.status}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <Row className="mb-2">
                        <Col sm={6}>
                          <Label className="col-form-label">
                            Document<span className="error">*</span>
                          </Label>
                          <KycDocumentUpload
                            value={validation?.values?.document}
                            placeholder="Select Document"
                            fieldKey={"document"}
                            validation={validation}
                            validateMaxSize={false}
                            extensionAllowedInput=".pdf"
                            extensionAllowed={["application/pdf"]}
                            typeError="Please upload file in PDF format only."
                          />
                          <FormText className="text-info d-block">
                            *Note:Leave Blank if do not want to update
                          </FormText>
                          <UploadedFileBox
                            label={"Document Preview"}
                            href={details?.document_url}
                          />
                        </Col>

                        <Col xl={6} className="mb-2">
                          <Label className="col-form-label">
                            Document Type<span className="error">*</span>
                          </Label>
                          <select
                            name="docType"
                            className="form-select pageSize mb-2"
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            value={validation.values.docType}
                          >
                            <option value={""}>Select Document Type</option>
                            {documentTypes.map((item, index) => (
                              <option
                                key={`doc_type_${index}`}
                                value={item.value}
                              >
                                {item.label}
                              </option>
                            ))}
                          </select>
                          {validation.touched.docType &&
                          validation.errors.docType ? (
                            <FormFeedback type="invalid">
                              {validation.errors.docType}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      </Row>

                      <Row className="justify-content-end mt-4">
                        <Col sm={12}>
                          <div className="d-flex justify-content-end align-items-center gap-3">
                            <Button
                              type="submit"
                              className="btn btn-primary w-md"
                              disabled={isUpdating}
                            >
                              {isUpdating ? <Spinners /> : "Update Document"}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default DocumentEdit
